import { DesktopOutlined, QuestionCircleOutlined, SendOutlined } from '@ant-design/icons'
import { App, Button, Switch, Tooltip } from 'antd'
import { useState } from 'react'

import { useDesktopPush } from '../../hooks/useDesktopPush'
import { disableDesktopPush, enableDesktopPush, pushApi } from '../../services/webPush'

const BLOCKED_HELP =
  'المتصفح يحظر تنبيهات هذا الموقع. افتح إعدادات الموقع من رمز القفل بجانب العنوان، ' +
  'اسمح بالإشعارات، ثم أعد تحميل الصفحة.'

/**
 * صف «تنبيهات سطح المكتب» أعلى قائمة الإشعارات: مفتاح تشغيل، وحالة الحظر مع الإرشاد،
 * وزر تنبيهٍ تجريبي ليتأكد الموظف أنها تصله فعلاً (تظهر بنظام التشغيل ولو أغلق التبويب).
 */
export function DesktopPushRow() {
  const { message } = App.useApp()
  const push = useDesktopPush()
  const [testing, setTesting] = useState(false)

  if (push.permission === 'unsupported') return null
  const blocked = push.permission === 'denied'
  const on = push.permission === 'granted' && push.subscribed

  const toggle = async (checked: boolean) => {
    if (checked) {
      // طلب الإذن يجب أن يكون بنقرة الموظف — هنا بالضبط
      const perm = await enableDesktopPush()
      if (perm === 'denied')
        message.warning('رُفض الإذن — يمكن السماح به من إعدادات الموقع بالمتصفح')
      else if (perm === 'granted') message.success('تنبيهات سطح المكتب مفعّلة ✓')
    } else {
      await disableDesktopPush()
      message.info('أُوقفت تنبيهات سطح المكتب لهذا المتصفح')
    }
  }

  const sendTest = async () => {
    setTesting(true)
    try {
      const { subscriptions } = await pushApi.test()
      if (subscriptions === 0) message.warning('لا يوجد متصفح مشترك — شغّل التنبيهات أولاً')
      else
        message.success('أُرسل تنبيه تجريبي — يظهر خلال ثوانٍ (أغلق التبويب لتراه بنظام التشغيل)')
    } catch {
      message.error('تعذّر إرسال التنبيه التجريبي')
    } finally {
      setTesting(false)
    }
  }

  return (
    <div className="desktop-push-row">
      <DesktopOutlined className="desktop-push-row__icon" />
      <div className="desktop-push-row__text">
        <div className="desktop-push-row__title">
          تنبيهات سطح المكتب
          {blocked && (
            <Tooltip title={BLOCKED_HELP}>
              <QuestionCircleOutlined style={{ marginInlineStart: 6, color: 'var(--aufq-warn)' }} />
            </Tooltip>
          )}
        </div>
        <div className="desktop-push-row__hint">
          {blocked
            ? 'محظورة من إعدادات المتصفح'
            : on
              ? 'تصلك الأسماء والمهام الجديدة حتى والتبويب مغلق'
              : 'فعّلها لتصلك الأسماء والمهام فور حدوثها — حتى والتبويب مغلق'}
        </div>
      </div>
      {on && (
        <Tooltip title="إرسال تنبيه تجريبي لهذا المتصفح">
          <Button
            type="text"
            size="small"
            icon={<SendOutlined />}
            loading={testing}
            onClick={sendTest}
            aria-label="إرسال تنبيه تجريبي"
          />
        </Tooltip>
      )}
      <Switch
        size="small"
        checked={on}
        disabled={blocked || push.busy}
        loading={push.busy}
        onChange={(checked) => void toggle(checked)}
        aria-label="تنبيهات سطح المكتب"
      />
    </div>
  )
}
